'use client';

import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaMousePointer, FaHandPointer } from 'react-icons/fa';

interface ElementSpotlightProps {
  selector: string;
  title: string;
}

interface ElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
  found: boolean;
}

const ElementSpotlight: React.FC<ElementSpotlightProps> = ({ selector, title }) => {
  const [elementPosition, setElementPosition] = useState<ElementPosition>({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    found: false
  });
  
  const spotlightRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Find the target element and calculate its position
  useEffect(() => {
    const findElement = () => {
      try {
        const element = document.querySelector(selector);
        
        if (element) {
          const rect = element.getBoundingClientRect();
          const padding = 8; // Add some padding around the element
          
          setElementPosition({
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + (padding * 2),
            height: rect.height + (padding * 2),
            found: true
          });
          
          // Scroll element into view if needed
          if (
            rect.top < 0 ||
            rect.left < 0 ||
            rect.bottom > window.innerHeight ||
            rect.right > window.innerWidth
          ) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        } else {
          console.log(`Element with selector ${selector} not found`);
          setElementPosition(prev => ({ ...prev, found: false }));
          
          // Retry after a delay
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(findElement, 500);
        }
      } catch (error) {
        console.error('Error finding element:', error);
        setElementPosition(prev => ({ ...prev, found: false }));
      }
    };

    findElement();
    
    // Set up window resize listener
    const handleResize = () => {
      findElement();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [selector]);

  // Helper function to determine best pointer position
  const getPointerPosition = () => {
    // Calculate center of element
    const centerX = elementPosition.left + elementPosition.width / 2;
    const centerY = elementPosition.top + elementPosition.height / 2;
    
    // Calculate center of viewport
    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;
    
    // Determine which quadrant the element is in
    const isTop = centerY < viewportCenterY;
    const isLeft = centerX < viewportCenterX;
    
    if (isTop && isLeft) return { position: 'bottom-right', label: 'top-left' };
    if (isTop && !isLeft) return { position: 'bottom-left', label: 'top-right' };
    if (!isTop && isLeft) return { position: 'top-right', label: 'bottom-left' };
    return { position: 'top-left', label: 'bottom-right' };
  };

  // Get action text based on title
  const getActionText = () => {
    if (title.includes('Filter')) return 'Filter Trails Here';
    if (title.includes('Navigation')) return 'Start Navigation Here';
    if (title.includes('Photo')) return 'Take Photos Here';
    if (title.includes('Details')) return 'View Details Here';
    if (title.includes('Map')) return 'Explore Map Here';
    if (title.includes('Adjust')) return 'Adjust Here';
    return 'Click Here';
  };

  if (!elementPosition.found) return null;

  const { position, label } = getPointerPosition();
  const actionText = getActionText();

  return (
    <div 
      ref={spotlightRef}
      className="fixed inset-0 pointer-events-none z-40"
    >
      {/* Spotlight overlay */}
      <div className="absolute" style={{
        top: `${elementPosition.top}px`,
        left: `${elementPosition.left}px`,
        width: `${elementPosition.width}px`,
        height: `${elementPosition.height}px`,
        boxShadow: '0 0 0 5000px rgba(0, 0, 0, 0.6)',
        border: '3px solid rgba(59, 130, 246, 0.8)',
        borderRadius: '4px',
        zIndex: 10
      }}>
        {/* Pulsing border effect */}
        <motion.div 
          className="absolute inset-0 rounded-sm"
          animate={{ 
            boxShadow: [
              '0 0 0 0 rgba(59, 130, 246, 0)', 
              '0 0 0 4px rgba(59, 130, 246, 0.4)', 
              '0 0 0 0 rgba(59, 130, 246, 0)'
            ] 
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>

      {/* Animated pointer */}
      <motion.div
        className={`absolute z-50 flex items-center ${getPointerPositionClass(position)}`}
        style={{
          top: position.includes('top') ? 
            elementPosition.top - 70 : 
            elementPosition.top + elementPosition.height + 10,
          left: position.includes('left') ? 
            elementPosition.left - 70 : 
            elementPosition.left + elementPosition.width + 10,
        }}
        animate={{ 
          y: [0, position.includes('top') ? 10 : -10, 0],
          opacity: [0.7, 1, 0.7] 
        }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="flex items-center bg-blue-500 text-white px-3 py-2 rounded-full shadow-lg">
          <FaHandPointer className="mr-2" />
          <span className="text-sm font-medium whitespace-nowrap">{actionText}</span>
        </div>
        <motion.div 
          className="absolute"
          style={{
            top: position.includes('top') ? '100%' : 'auto',
            bottom: position.includes('bottom') ? '100%' : 'auto',
            left: position.includes('left-label') ? '50%' : 'auto',
            right: position.includes('right-label') ? '50%' : 'auto',
            transform: 'translateX(-50%)',
          }}
          animate={{ y: position.includes('top') ? [0, -5, 0] : [0, 5, 0] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          {position.includes('top') ? (
            <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-blue-500" />
          ) : (
            <div className="w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-blue-500" />
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

// Helper function to get position classes
function getPointerPositionClass(position: string) {
  switch (position) {
    case 'top-left': return 'flex-col-reverse items-start';
    case 'top-right': return 'flex-col-reverse items-end';
    case 'bottom-left': return 'flex-col items-start';
    case 'bottom-right': return 'flex-col items-end';
    default: return 'flex-col items-center';
  }
}

export default ElementSpotlight;